import { AgentActivity } from '@numeo/types'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from 'store'
import agentActivitiesApi, { AgentActivityFilters } from '../services/agentActivitiesApi'

export interface AgentActivitiesState {
    activities: AgentActivity[]
    filters: AgentActivityFilters
    loading: boolean
    error: string | null
    lastUpdated?: number
    toolbarVisible: boolean
    detailSidebarOpen: boolean
    recentActivities: AgentActivity[]
    lastActivityTime: number | null
}

const initialState: AgentActivitiesState = {
    activities: [],
    filters: {
        projectId: '', // Will be populated from thunk
    },
    loading: false,
    error: null,
    toolbarVisible: false,
    detailSidebarOpen: false,
    recentActivities: [],
    lastActivityTime: null,
}

export const fetchAgentActivities = createAsyncThunk('agentActivities/fetch', async (filters: AgentActivityFilters, { rejectWithValue, getState }) => {
    try {
        const state = getState() as RootState
        const projectId = state.auth.application?.projectName

        if (!projectId) {
            return rejectWithValue('Project ID not found')
        }

        const mergedFilters = { ...filters, projectId }
        const data = await agentActivitiesApi.getAgentActivities(mergedFilters)
        return data
    } catch (error) {
        return rejectWithValue((error as Error).message)
    }
})

const agentActivitiesSlice = createSlice({
    name: 'agentActivities',
    initialState,
    reducers: {
        setFilters(state, action: PayloadAction<AgentActivityFilters>) {
            state.filters = action.payload
        },
        clearActivities(state) {
            state.activities = []
        },
        addActivity(state, action: PayloadAction<AgentActivity>) {
            state.activities.unshift(action.payload)
        },
        showToolbar(state) {
            state.toolbarVisible = true
        },
        hideToolbar(state) {
            state.toolbarVisible = false
        },
        openDetailSidebar(state) {
            state.detailSidebarOpen = true
            // Clear recent activities to hide the badge
            state.recentActivities = []
            state.lastActivityTime = null
        },
        closeDetailSidebar(state) {
            state.detailSidebarOpen = false
        },
        addRecentActivity(state, action: PayloadAction<AgentActivity>) {
            state.recentActivities.unshift(action.payload)
            // Keep only last 10 recent activities
            if (state.recentActivities.length > 10) {
                state.recentActivities = state.recentActivities.slice(0, 10)
            }
            state.lastActivityTime = Date.now()
            state.toolbarVisible = true
        },
        clearRecentActivities(state) {
            state.recentActivities = []
            state.lastActivityTime = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAgentActivities.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchAgentActivities.fulfilled, (state, action) => {
                state.loading = false
                state.activities = action.payload
                state.lastUpdated = Date.now()
            })
            .addCase(fetchAgentActivities.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload as string
            })
    },
})

export const {
    setFilters,
    clearActivities,
    addActivity,
    showToolbar,
    hideToolbar,
    openDetailSidebar,
    closeDetailSidebar,
    addRecentActivity,
    clearRecentActivities,
} = agentActivitiesSlice.actions

export const selectAgentActivities = (state: RootState) => state.agentActivities.activities
export const selectAgentActivitiesLoading = (state: RootState) => state.agentActivities.loading
export const selectAgentActivitiesError = (state: RootState) => state.agentActivities.error
export const selectToolbarVisible = (state: RootState) => state.agentActivities.toolbarVisible
export const selectDetailSidebarOpen = (state: RootState) => state.agentActivities.detailSidebarOpen
export const selectRecentActivities = (state: RootState) => state.agentActivities.recentActivities
export const selectLastActivityTime = (state: RootState) => state.agentActivities.lastActivityTime

export default agentActivitiesSlice.reducer
